import { parseFormula, collectVariableNames } from './parser'
import { evaluateFormula } from './evaluator'
import { builtinFunctions } from './functions'
import { ObjectValueProvider } from './provider'

/** 테스트 헬퍼: 수식 + 변수값 맵 → 평가 결과 */
async function evalWith(formula: string, values: Record<string, any> = {}): Promise<any> {
  const ast = parseFormula(formula)
  return evaluateFormula(ast, { functions: builtinFunctions, provider: new ObjectValueProvider(values) })
}

describe('parser (parseFormula)', () => {
  it('숫자/소수를 파싱한다', () => {
    expect(parseFormula('42')).toEqual({ type: 'number', value: 42 })
    expect(parseFormula('3.14')).toEqual({ type: 'number', value: 3.14 })
  })

  it('대괄호 변수(한글·공백 포함)를 파싱하고 대괄호를 벗긴다', () => {
    expect(parseFormula('[재해 건수]')).toEqual({ type: 'variable', name: '재해 건수' })
  })

  it('bare 식별자를 변수로 파싱한다', () => {
    expect(parseFormula('x11')).toEqual({ type: 'variable', name: 'x11' })
  })

  it('사칙연산 우선순위(*,/ 가 +,- 보다 높음)를 지킨다', () => {
    const ast = parseFormula('1 + 2 * 3')
    // + 가 최상위, 오른쪽이 (2*3)
    expect(ast).toMatchObject({ type: 'binary', op: '+', right: { type: 'binary', op: '*' } })
  })

  it('괄호로 우선순위를 바꾼다', () => {
    const ast = parseFormula('(1 + 2) * 3')
    expect(ast).toMatchObject({ type: 'binary', op: '*', left: { type: 'binary', op: '+' } })
  })

  it('단항 마이너스를 파싱한다', () => {
    expect(parseFormula('-5')).toEqual({ type: 'unary', op: '-', arg: { type: 'number', value: 5 } })
  })

  it('함수 호출을 파싱한다', () => {
    const ast = parseFormula('sum(1, 2, 3)')
    expect(ast).toMatchObject({ type: 'function', name: 'sum' })
    expect((ast as any).args).toHaveLength(3)
  })

  it('잘못된 토큰/불완전 수식은 throw 한다', () => {
    expect(() => parseFormula('1 @ 2')).toThrow()
    expect(() => parseFormula('[a] +')).toThrow()
    expect(() => parseFormula('(1 + 2')).toThrow()
  })
})

describe('collectVariableNames', () => {
  it('대괄호 한글 변수명을 정확히 수집한다 (정규식이 놓치는 케이스)', () => {
    const ast = parseFormula('[실제공사기간] / [연면적]')
    expect(collectVariableNames(ast).sort()).toEqual(['실제공사기간', '연면적'].sort())
  })

  it('중복을 제거한다', () => {
    const ast = parseFormula('([계획공사비] - [계획공사비]) / [계획공사비]')
    expect(collectVariableNames(ast)).toEqual(['계획공사비'])
  })

  it('함수 인자 안의 변수도 수집한다', () => {
    const ast = parseFormula('round([a] / [b], 2)')
    expect(collectVariableNames(ast).sort()).toEqual(['a', 'b'])
  })

  it('변수가 없으면 빈 배열', () => {
    expect(collectVariableNames(parseFormula('1 + 2'))).toEqual([])
  })
})

describe('evaluator — 연산', () => {
  it('사칙연산과 우선순위', async () => {
    expect(await evalWith('1 + 2 * 3')).toBe(7)
    expect(await evalWith('(1 + 2) * 3')).toBe(9)
    expect(await evalWith('10 / 4')).toBe(2.5)
    expect(await evalWith('-5 + 3')).toBe(-2)
  })

  it('변수를 provider 로 해석한다 (대괄호 한글 포함)', async () => {
    expect(await evalWith('[a] / [b]', { a: 10, b: 2 })).toBe(5)
    expect(await evalWith('[재해 건수] / [연간 근로자 수] * 100', { '재해 건수': 5, '연간 근로자 수': 1000 })).toBeCloseTo(0.5)
  })

  it('알 수 없는 함수는 throw', async () => {
    await expect(evalWith('nope(1)')).rejects.toThrow(/Unknown function/)
  })
})

describe('builtinFunctions', () => {
  it('sum/avg/min/max/round/if', async () => {
    expect(await evalWith('sum(1, 2, 3)')).toBe(6)
    expect(await evalWith('avg(2, 4, 6)')).toBe(4)
    expect(await evalWith('min(3, 1, 2)')).toBe(1)
    expect(await evalWith('max(3, 1, 2)')).toBe(3)
    expect(await evalWith('round(3.14159, 2)')).toBe(3.14)
    expect(await evalWith('if(1, 10, 20)')).toBe(10)
    expect(await evalWith('if(0, 10, 20)')).toBe(20)
  })
})

/**
 * 회귀 방어: kpi 모듈 seed(kpis-seed.json)의 실제 formula 코퍼스.
 * 이 formula들은 dkpi 런타임에서 AST 엔진으로 평가되므로, 파싱·변수추출·평가가
 * 깨지면 dkpi 가 깨진다. (P0-1: aggregate 경로도 이제 이 엔진을 씀)
 */
describe('seed formula 코퍼스 (회귀 방어)', () => {
  const SEED_FORMULAS = [
    '[실제공사기간] / [연면적]',
    '([실제공사기간] - [계획공사기간]) / [계획공사기간]',
    '[일정 이탈 수준]',
    '[실제공사비] / [연면적]',
    '([실제공사비]-[계획공사비]) / [계획공사비]',
    '[품질시험 불합격 건수] / [연면적]',
    '[재해 건수] / [연간 근로자 수] * 100',
    '[노동손실일수] /  ( [총 근로자수] * [1인당 연간 근로 시간] )  * 1000',
    '[총 건설 폐기물 발생량] / [연면적]',
    '[투입인력] / [연면적]'
  ]

  it.each(SEED_FORMULAS)('파싱되고 변수추출된다: %s', formula => {
    const ast = parseFormula(formula)
    const vars = collectVariableNames(ast)
    expect(Array.isArray(vars)).toBe(true)
    // 단일 변수 formula 도 최소 1개 이상
    expect(vars.length).toBeGreaterThanOrEqual(1)
  })

  it('대표 formula 들이 올바른 수치를 낸다', async () => {
    expect(await evalWith('[실제공사기간] / [연면적]', { 실제공사기간: 300, 연면적: 1500 })).toBeCloseTo(0.2)
    expect(
      await evalWith('([실제공사비]-[계획공사비]) / [계획공사비]', { 실제공사비: 1200, 계획공사비: 1000 })
    ).toBeCloseTo(0.2)
    expect(
      await evalWith('[노동손실일수] /  ( [총 근로자수] * [1인당 연간 근로 시간] )  * 1000', {
        노동손실일수: 10,
        '총 근로자수': 100,
        '1인당 연간 근로 시간': 2000
      })
    ).toBeCloseTo(0.05)
    // 단일 변수 pass-through
    expect(await evalWith('[일정 이탈 수준]', { '일정 이탈 수준': 3 })).toBe(3)
  })
})
